/**
 * Guided Tier-2 editors (spec 2026-07-04 §7): the read-only overview
 * sections flip to these in edit mode. Every input writes through the
 * workflowConfigStore's pruning setters — the overlay stays sparse.
 */
import { useRef, useState } from 'react'
import { useWorkflowConfigStore } from '@/stores/workflowConfigStore'
import { extractTokens, tokenLabel } from '@/utils/friendlyLabels'
import { hasSegmentSyntax } from '@/utils/templateSegments'
import { InlineFindings } from './entryControls'
import type { OrderWorkflowDefinitionJson } from '@/types/workflowDefinition'
import type { AgendaItemVM } from '@/utils/workflowOverview'

export function BilingualTextEditor({ value, baseValue, onChange, tokenLegend, languages }: {
  value: Record<string, string>
  baseValue: Record<string, string>
  onChange: (lang: string, text: string) => void
  /** Raw tokens (e.g. '$slots.customer_name') to offer as insertable chips. */
  tokenLegend?: string[]
  /** Canonical language list (definition `languages`). When provided, every
   * listed language renders a textarea even with no text yet (SA-83: an
   * orphaned entry's missing ask language must be enterable). Stray languages
   * present only in the maps still render after them — never hide data. */
  languages?: string[]
}) {
  const langs = Array.from(
    new Set([...(languages ?? []), ...Object.keys(baseValue), ...Object.keys(value)]),
  )
  const areaRefs = useRef<Record<string, HTMLTextAreaElement | null>>({})
  const lastLang = useRef<string | null>(null)

  const insertToken = (token: string) => {
    const lang = lastLang.current ?? langs[0]
    if (!lang) return
    const current = value[lang] ?? baseValue[lang] ?? ''
    const el = areaRefs.current[lang]
    // No focus yet → append; otherwise honor the focused textarea's cursor.
    const pos = lastLang.current === null ? current.length : (el?.selectionStart ?? current.length)
    onChange(lang, current.slice(0, pos) + token + current.slice(pos))
  }

  return (
    <div className="space-y-1.5">
      <div className="grid gap-2 sm:grid-cols-2">
        {langs.map((lang) => {
          const changed = value[lang] !== undefined && value[lang] !== baseValue[lang]
          return (
            <label key={lang} className="block text-xs">
              <span className="font-mono uppercase text-muted-foreground mr-1">
                {lang}
                {changed && (
                  <span className="ml-1 text-primary">• edited</span>
                )}
              </span>
              <textarea
                ref={(el) => { areaRefs.current[lang] = el }}
                onFocus={() => { lastLang.current = lang }}
                className="mt-1 w-full rounded-sm border border-border bg-background p-2 text-xs text-foreground"
                rows={2}
                value={value[lang] ?? baseValue[lang] ?? ''}
                onChange={(e) => onChange(lang, e.target.value)}
              />
            </label>
          )
        })}
      </div>
      {tokenLegend && tokenLegend.length > 0 && (
        <p className="flex flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground">
          Pieces you can use:
          {tokenLegend.map((token) => (
            <button
              key={token}
              type="button"
              aria-label={`insert ${token}`}
              title={token}
              onClick={() => insertToken(token)}
              className="inline-flex items-center rounded-md border border-primary/20 bg-primary/10 px-1.5 text-[11px] font-medium text-foreground hover:border-primary"
            >
              {tokenLabel(token)}
            </button>
          ))}
        </p>
      )}
    </div>
  )
}

/**
 * Single-string sibling of BilingualTextEditor for an agenda item's
 * `prompt_hint` ("Exact instructions to the AI"). Reset-to-base is implicit:
 * the store setter prunes when the value equals the base (an "• edited" badge
 * shows when it differs). Base $slots refs are offered as insertable chips.
 */
function PromptHintEditor({ value, baseValue, onChange }: {
  value: string
  baseValue: string
  onChange: (text: string) => void
}) {
  const ref = useRef<HTMLTextAreaElement | null>(null)
  const changed = value !== baseValue
  const tokens = extractTokens({ hint: baseValue })
  const insertToken = (token: string) => {
    const el = ref.current
    const pos = el?.selectionStart ?? value.length
    onChange(value.slice(0, pos) + token + value.slice(pos))
  }
  return (
    <div className="space-y-1.5">
      {/* Token chips live OUTSIDE the <label> — a wrapping label would
          otherwise become the accessible name of every chip button too. */}
      <label className="block text-xs">
        <span className="text-muted-foreground">
          Exact instructions to the AI
          {changed && <span className="ml-1 text-primary">• edited</span>}
        </span>
        <textarea
          ref={ref}
          aria-label="exact instructions to the AI"
          rows={2}
          className="mt-1 w-full rounded-sm border border-border bg-background p-2 text-xs text-foreground"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      </label>
      {tokens.length > 0 && (
        <p className="flex flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground">
          Pieces you can use:
          {tokens.map((token) => (
            <button
              key={token}
              type="button"
              aria-label={`insert ${token}`}
              title={token}
              onClick={() => insertToken(token)}
              className="inline-flex items-center rounded-md border border-primary/20 bg-primary/10 px-1.5 text-[11px] font-medium text-foreground hover:border-primary"
            >
              {tokenLabel(token)}
            </button>
          ))}
        </p>
      )}
    </div>
  )
}

export function AskItemEditor({ item, languages }: {
  item: AgendaItemVM
  languages?: string[]
}) {
  // Base values come from the projected item (asks/priority/requiredness);
  // the store's setters read the full base definition internally to prune.
  const store = useWorkflowConfigStore()
  const overlaySlot = store.overlay.slots?.[item.key] as Record<string, unknown> | undefined
  // Needs live in their own overlay section (setAskPriority routes them
  // there) — read the effective priority from the matching section.
  const overlayEntry =
    item.itemKind === 'need'
      ? (store.overlay.needs?.[item.key] as Record<string, unknown> | undefined)
      : overlaySlot
  const overlayAsks = store.overlay.conversation?.asks?.[item.key] ?? {}
  const effectiveAsks = { ...item.asks, ...overlayAsks }
  const effectivePriority = (overlayEntry?.ask_priority as number | undefined) ?? item.askPriority
  const effectiveRequired =
    (overlaySlot?.always_required as boolean | undefined) ??
    (item.requiredness === 'always')
  const effectivePromptHint =
    (overlayEntry?.prompt_hint as string | undefined) ?? item.promptHint ?? ''

  return (
    <div className="space-y-2">
      <BilingualTextEditor
        value={effectiveAsks}
        baseValue={item.asks}
        languages={languages}
        onChange={(lang, text) => store.setAskText(item.key, lang, text)}
      />
      <PromptHintEditor
        value={effectivePromptHint}
        baseValue={item.promptHint ?? ''}
        onChange={(text) => store.setPromptHint(item.key, text)}
      />
      <div className="flex flex-wrap items-center gap-4 text-xs text-foreground">
        <label className="flex items-center gap-2">
          Ask order
          <input
            aria-label={`ask order for ${item.label}`}
            type="number"
            className="w-20 rounded-sm border border-border bg-background p-1"
            value={effectivePriority}
            onChange={(e) => store.setAskPriority(item.key, Number(e.target.value))}
          />
          <span className="text-muted-foreground">(lower = asked earlier)</span>
        </label>
        {item.itemKind === 'slot' && item.requiredness !== 'conditional' && (
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={effectiveRequired}
              onChange={(e) => store.setAlwaysRequired(item.key, e.target.checked)}
            />
            Always required
          </label>
        )}
        {item.itemKind === 'slot' && item.requiredness === 'conditional' && (
          <span className="text-muted-foreground">
            Asked conditionally ({item.condition}) — conditions are edited in a later phase.
          </span>
        )}
        {item.options && (
          <ChoicesInput
            key={item.key}
            label={item.label}
            itemKey={item.key}
            options={((overlaySlot?.options as string[] | undefined) ?? item.options) || []}
          />
        )}
      </div>
    </div>
  )
}

/**
 * Free-typing comma-separated options input. Parsing on every keystroke
 * (split/trim/filter/join) eats the comma character as it's typed, so a
 * second option can never be entered — instead we keep an uncontrolled-ish
 * local text buffer and only parse-and-commit to the store on blur. The
 * `key={item.key}` on the call site resets this local state when the user
 * switches between ask items.
 */
function ChoicesInput({ label, itemKey, options }: {
  label: string
  itemKey: string
  options: string[]
}) {
  const setOptions = useWorkflowConfigStore((s) => s.setOptions)
  const [text, setText] = useState(() => options.join(', '))

  return (
    <label className="flex items-center gap-2">
      Choices
      <input
        aria-label={`choices for ${label}`}
        className="w-56 rounded-sm border border-border bg-background p-1"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={() =>
          setOptions(
            itemKey,
            text.split(',').map((s) => s.trim()).filter(Boolean),
          )
        }
      />
    </label>
  )
}

export function TemplateEditor({ templateKey, base }: {
  templateKey: string
  base: OrderWorkflowDefinitionJson
}) {
  const store = useWorkflowConfigStore()
  const baseText = base.templates?.[templateKey] ?? {}
  const effective = { ...baseText, ...(store.overlay.templates?.[templateKey] ?? {}) }
  // SA-308: keyed off the LIVE effective text so typing `[[` shows the hint
  // at once; the server's validate finding (segment balance) lands inline.
  const segmented = Object.values(effective).some(hasSegmentSyntax)
  return (
    <div className="space-y-1">
      <BilingualTextEditor
        value={effective}
        baseValue={baseText}
        onChange={(lang, text) => store.setTemplateText(templateKey, lang, text)}
        tokenLegend={extractTokens(baseText)}
      />
      {segmented && (
        <p className="text-[11px] text-muted-foreground">
          Text inside [[ ]] is shown only when its values are known.
        </p>
      )}
      <InlineFindings paths={[`templates.${templateKey}`]} />
    </div>
  )
}

export function ParkMessageEditor({ base }: { base: OrderWorkflowDefinitionJson }) {
  const store = useWorkflowConfigStore()
  const baseText = base.conversation?.park_message ?? {}
  const effective = { ...baseText, ...(store.overlay.conversation?.park_message ?? {}) }
  return (
    <BilingualTextEditor
      value={effective}
      baseValue={baseText}
      onChange={(lang, text) => store.setParkMessage(lang, text)}
      tokenLegend={extractTokens(baseText)}
    />
  )
}

export function CooldownEditor({ base }: { base: OrderWorkflowDefinitionJson }) {
  const store = useWorkflowConfigStore()
  const overlayValue = store.overlay.settings?.post_completion_cooldown_minutes as
    | number
    | undefined
  const effective = overlayValue ?? base.post_completion_cooldown_minutes ?? null
  return (
    <label className="flex items-center gap-2 text-xs text-foreground">
      Pause after a completed order (minutes)
      <input
        aria-label="post-completion cooldown minutes"
        type="number"
        min={0}
        className="w-20 rounded-sm border border-border bg-background p-1"
        value={effective ?? ''}
        onChange={(e) =>
          store.setCooldown(e.target.value === '' ? null : Number(e.target.value))
        }
      />
    </label>
  )
}
